import { createRuntimeEvidenceRecord } from '../runtime';
import { buildGovernorDomainSupervisorReport } from '../domain-supervisor';
import {
  TREE_GEOMETRY_FAILURE_SOURCE_SCHEMA,
  TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS,
  type TreeGeometryEvidenceReport,
  type TreeGeometryRuntimeFatalRuleKey,
} from '../treeGeometryEvidence';
import {
  publishGovernorRuntimeEvidence,
  type GovernorRuntimeEvidenceEvent,
} from '../runtime-evidence/GovernorRuntimeEvidenceBus';
import {
  recordRuntimeEvidence,
} from '../runtime-evidence/RuntimeEvidenceEngine';
import type {
  CanonicalTreeRuntimeAuthorityConvergenceResult,
} from '../tree/CanonicalTreeActiveRuntimeAuthorityConvergenceContract';
import {
  resolveCanonicalTreeRuntimeConvergenceContainment,
  type CanonicalTreeRuntimeContainmentAction,
  type CanonicalTreeRuntimeContainmentStatus,
} from '../tree/CanonicalTreeRuntimeConvergenceContainmentAuthority';

import {
  projectTreeRuntimeConvergenceEvidence,
} from './TreeRuntimeConvergenceEvidenceProjection';

import {
  writeTreeRuntimeConvergenceSessionEvidence,
} from './TreeRuntimeConvergenceSessionEvidence';
import {
  TREE_RUNTIME_SELECTOR_DIAGNOSTIC_KEYS,
  type TreeRuntimeSelectorDiagnostics,
} from './TreeRuntimeSelectorDiagnostics';
import {
  TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS,
  type TreeRuntimeSelectorDiscrepancyClassification,
} from './TreeRuntimeSelectorDiscrepancyClassification';
import {
  TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS,
  type TreeRuntimeSelectorDiscrepancySourceClassification,
} from './TreeRuntimeSelectorDiscrepancySourceClassification';

import {
  LEGACY_ANCESTRY_HORIZONTAL_RULES,
  TREE_RUNTIME_LEGACY_ANCESTRY_CAUSAL_SCHEMA,
  type LegacyAncestryCausalLocalization,
} from './TreeRuntimeLegacyAncestryResidueCausalLocalization';

import {
  TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
  type TreeRuntimeCanonicalBranchCounterfactualComparison,
} from './TreeRuntimeCanonicalBranchCounterfactualShadowProjection';

import {
  TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA,
  type TreeRuntimeBranchLayoutInteractionFourCellComparison,
} from './TreeRuntimeBranchLayoutInteractionCounterfactualComparison';

import {
  CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS,
} from '../tree/CanonicalTreeConnectorCorridorAuthority';



import type {
  TreeRuntimeLifecycleAttributionStatus,
} from './TreeRuntimeMissingParentLifecycleAttributionParity';

import {
  projectTreeRuntimeMissingParentLifecycleAttributionPublication,
} from './TreeRuntimeMissingParentLifecycleAttributionPublicationContract';

import {
  projectTreeRuntimeMissingParentIdentityParityPublication,
} from './TreeRuntimeMissingParentIdentityParityPublicationContract';

import type {
  TreeRuntimeMissingParentIdentityBrowserObservation,
} from './TreeRuntimeMissingParentIdentityParityBrowserRuntime';

import {
  publishTreeRuntimeMissingParentLifecycleOperationalObservation,
} from './TreeRuntimeMissingParentLifecycleAttributionOperationalObservability';

export type TreeRuntimeIssueType =
  | 'FLOATING_RELATIVE'
  | 'MISSING_COPARENT_CHILD_LINK'
  | 'MISSING_CONNECTOR_PROMPT'
  | 'DUPLICATE_MEMBER'
  | 'ORPHANED_MEMBER'
  | 'SIDE_ISOLATION_RISK'
  | 'UNKNOWN_TREE_STATE';

export type TreeRuntimeObservation = {
  traceId: string;
  issueType: TreeRuntimeIssueType;
  memberId?: string;
  relatedMemberId?: string;
  summary: string;
  evidenceSource: string;
  repairExecuted: false;
};

export type TreeRuntimeGovernorBridgeReport = {
  version: string;
  generatedAt: string;
  readOnly: true;
  repairExecuted: false;
  observedIssues: number;
  runtimeEvidenceCreated: boolean;
  domainSupervisorStatus: string;
  status: 'PASS' | 'WARNING';
};

export const TREE_RUNTIME_GOVERNOR_BRIDGE_VERSION = 'CP-016.1';

export function buildTreeRuntimeGovernorBridgeReport(
  observations: TreeRuntimeObservation[] = []
): TreeRuntimeGovernorBridgeReport {
  const domainSupervisor = buildGovernorDomainSupervisorReport();

  const evidence = createRuntimeEvidenceRecord({
    traceId: `tree-runtime-${Date.now()}`,
    domain: 'tree',
    status: observations.length ? 'OBSERVED' : 'VERIFIED',
    confidence: 'runtime',
    summary: observations.length
      ? `Tree Governor observed ${observations.length} tree runtime issue(s).`
      : 'Tree Governor runtime bridge initialized with no observed issues.',
    dependencies: ['tree', 'relationship', 'runtime', 'domain-supervisor'],
    source: 'TreeRuntimeGovernorBridge',
  });

  return {
    version: TREE_RUNTIME_GOVERNOR_BRIDGE_VERSION,
    generatedAt: new Date().toISOString(),
    readOnly: true,
    repairExecuted: false,
    observedIssues: observations.length,
    runtimeEvidenceCreated: !!evidence.id,
    domainSupervisorStatus: domainSupervisor.supervisorStatus,
    status: observations.length ? 'WARNING' : 'PASS',
  };
}

export type TreeGeometryRuntimeFailureSourceSummary = {
  readonly fatalRuleCounts:
    Readonly<
      Record<
        TreeGeometryRuntimeFatalRuleKey,
        number
      >
    >;

  readonly fatalKnownIssueCount: number;
  readonly fatalUnknownIssueCount: number;

  readonly informationalIssueCount: number;
  readonly passIssueCount: number;
  readonly unclassifiedSeverityCount: number;

  readonly routeRejectedNonadjacentGenerationCount:
    number;

  readonly routeRejectedRootBoundaryCount:
    number;

  readonly routeRejectedObstacleIntersectionCount:
    number;

  readonly routeRejectedUnclassifiedCount:
    number;

  readonly readOnly: true;
  readonly repairExecuted: false;
};

export function classifyTreeGeometryRuntimeFailureSources(
  report:
    TreeGeometryEvidenceReport,
): TreeGeometryRuntimeFailureSourceSummary {
  const fatalRuleCounts =
    Object.fromEntries(
      TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.map(
        rule => [rule, 0],
      ),
    ) as Record<
      TreeGeometryRuntimeFatalRuleKey,
      number
    >;

  const knownFatalRules =
    new Set<string>(
      TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS,
    );

  let fatalUnknownIssueCount = 0;
  let informationalIssueCount = 0;
  let passIssueCount = 0;
  let unclassifiedSeverityCount = 0;

  let routeRejectedNonadjacentGenerationCount =
    0;

  let routeRejectedRootBoundaryCount =
    0;

  let routeRejectedObstacleIntersectionCount =
    0;

  let routeRejectedUnclassifiedCount =
    0;

  for (const issue of report.issues) {
    if (issue.status === 'FAIL') {
      if (
        knownFatalRules.has(
          issue.rule,
        )
      ) {
        fatalRuleCounts[
          issue.rule as
            TreeGeometryRuntimeFatalRuleKey
        ] += 1;
      } else {
        fatalUnknownIssueCount += 1;
      }

      if (
        issue.rule ===
        'GEOMETRY-CONNECTOR-ROUTE-REJECTED'
      ) {
        switch (
          issue.routeRejectionStatus
        ) {
          case 'REJECTED-NONADJACENT-GENERATION':
            routeRejectedNonadjacentGenerationCount +=
              1;
            break;

          case 'REJECTED-ROOT-BOUNDARY':
            routeRejectedRootBoundaryCount +=
              1;
            break;

          case 'REJECTED-OBSTACLE-INTERSECTION':
            routeRejectedObstacleIntersectionCount +=
              1;
            break;

          default:
            routeRejectedUnclassifiedCount +=
              1;
            break;
        }
      }

      continue;
    }

    if (issue.status === 'WARNING') {
      continue;
    }

    if (issue.status === 'INFO') {
      informationalIssueCount += 1;
      continue;
    }

    if (issue.status === 'PASS') {
      passIssueCount += 1;
      continue;
    }

    unclassifiedSeverityCount += 1;
  }

  const fatalKnownIssueCount =
    TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.reduce(
      (total, rule) =>
        total +
        fatalRuleCounts[rule],
      0,
    );

  return Object.freeze({
    fatalRuleCounts:
      Object.freeze({
        ...fatalRuleCounts,
      }),

    fatalKnownIssueCount,
    fatalUnknownIssueCount,

    informationalIssueCount,
    passIssueCount,
    unclassifiedSeverityCount,

    routeRejectedNonadjacentGenerationCount,
    routeRejectedRootBoundaryCount,
    routeRejectedObstacleIntersectionCount,
    routeRejectedUnclassifiedCount,

    readOnly: true,
    repairExecuted: false,
  });
}

export type TreeGeometryRuntimeEvidencePublication = {
  readOnly: true;
  repairExecuted: false;
  authenticatedSession: boolean;
  published: boolean;
  status: 'PASS' | 'FAIL';
  issueCount: number;
  fatalIssueCount: number;
  warningIssueCount: number;

  failureSources:
    TreeGeometryRuntimeFailureSourceSummary;

  parentConnectorMissingCount: number;
  parentConnectorUnroutedCount: number;
  routeRejectedCount: number;
  routeMissingCount: number;
  parentEndpointAuthorityFailureCount: number;
  sharedEvidenceId: string | null;
  runtimeEvidenceRecorded: boolean;
  runtimeEvidencePublished: boolean;
};

export function publishTreeGeometryRuntimeEvidence(
  report: TreeGeometryEvidenceReport,
  authenticatedSession: boolean,
  geometryObservationId:
    string | null = null,
): TreeGeometryRuntimeEvidencePublication {
  const fatalIssueCount = report.issues.filter(
    (issue) => issue.status === 'FAIL',
  ).length;

  const warningIssueCount = report.issues.filter(
    (issue) => issue.status === 'WARNING',
  ).length;

  const failureSources =
    classifyTreeGeometryRuntimeFailureSources(
      report,
    );

  const countRule = (
    rule: string,
  ): number =>
    report.issues.filter(
      issue => issue.rule === rule,
    ).length;

  const parentConnectorMissingCount =
    countRule(
      'GEOMETRY-PARENT-CONNECTOR-MISSING',
    );

  const parentConnectorUnroutedCount =
    countRule(
      'GEOMETRY-PARENT-CONNECTOR-UNROUTED',
    );

  const routeRejectedCount =
    countRule(
      'GEOMETRY-CONNECTOR-ROUTE-REJECTED',
    );

  const routeMissingCount =
    countRule(
      'GEOMETRY-CONNECTOR-ROUTE-MISSING',
    );

  const parentEndpointAuthorityFailureCount =
    countRule(
      'GEOMETRY-PARENT-ENDPOINT-AUTHORITY',
    );

  if (!authenticatedSession) {
    return {
      readOnly: true,
      repairExecuted: false,
      authenticatedSession: false,
      published: false,
      status: report.status,
      issueCount: report.issueCount,
      fatalIssueCount,
      warningIssueCount,
      failureSources,
      parentConnectorMissingCount,
      parentConnectorUnroutedCount,
      routeRejectedCount,
      routeMissingCount,
      parentEndpointAuthorityFailureCount,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    };
  }

  const observedAt = new Date().toISOString();
  const evidenceId =
    `tree-geometry-${Date.now()}-${report.cardCount}`;

  const summary =
    report.status === 'FAIL'
      ? `Tree geometry evidence detected ${fatalIssueCount} fatal issue(s) across ${report.cardCount} card(s), ${report.edgeCount} edge(s), ${report.spouseLinkCount} spouse link(s), and ${report.companionChipCount} companion chip(s).`
      : `Tree geometry evidence verified ${report.cardCount} card(s), ${report.edgeCount} edge(s), ${report.spouseLinkCount} spouse link(s), and ${report.companionChipCount} companion chip(s).`;

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null = null;

  let runtimeEvidenceRecorded = false;

  try {
    sharedEvidence = publishGovernorRuntimeEvidence({
      domain: 'tree',
      source: 'TreeGeometryEvidence',
      severity:
        report.status === 'FAIL'
          ? 'fail'
          : warningIssueCount > 0
            ? 'warn'
            : 'info',
      summary,
      evidence: [
        `evidenceId=${evidenceId}`,
        `authenticatedSession=true`,
        ...(
          geometryObservationId
            ? [
                `geometryObservationId=${geometryObservationId}`,
              ]
            : []
        ),
        `failureSourceSchema=${TREE_GEOMETRY_FAILURE_SOURCE_SCHEMA}`,
        `status=${report.status}`,
        `issueCount=${report.issueCount}`,
        `fatalIssueCount=${fatalIssueCount}`,
        `warningIssueCount=${warningIssueCount}`,
        `fatalKnownIssueCount=${failureSources.fatalKnownIssueCount}`,
        `fatalUnknownIssueCount=${failureSources.fatalUnknownIssueCount}`,
        `informationalIssueCount=${failureSources.informationalIssueCount}`,
        `passIssueCount=${failureSources.passIssueCount}`,
        `unclassifiedSeverityCount=${failureSources.unclassifiedSeverityCount}`,
        `routeRejectedNonadjacentGenerationCount=${failureSources.routeRejectedNonadjacentGenerationCount}`,
        `routeRejectedRootBoundaryCount=${failureSources.routeRejectedRootBoundaryCount}`,
        `routeRejectedObstacleIntersectionCount=${failureSources.routeRejectedObstacleIntersectionCount}`,
        `routeRejectedUnclassifiedCount=${failureSources.routeRejectedUnclassifiedCount}`,
        ...TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.map(
          rule =>
            `fatalRule.${rule}=${failureSources.fatalRuleCounts[rule]}`,
        ),
        `parentConnectorMissingCount=${parentConnectorMissingCount}`,
        `parentConnectorUnroutedCount=${parentConnectorUnroutedCount}`,
        `routeRejectedCount=${routeRejectedCount}`,
        `routeMissingCount=${routeMissingCount}`,
        `parentEndpointAuthorityFailureCount=${parentEndpointAuthorityFailureCount}`,
        `cardCount=${report.cardCount}`,
        `edgeCount=${report.edgeCount}`,
        `spouseLinkCount=${report.spouseLinkCount}`,
        `companionChipCount=${report.companionChipCount}`,
        `observedAt=${observedAt}`,
      ],
      owner: 'Tree Governor',
      repairReady: false,
    });
  } catch {
    sharedEvidence = null;
  }

  try {
    recordRuntimeEvidence({
      id: evidenceId,
      governor: 'tree-governor',
      source: 'TreeGeometryEvidence',
      event:
        report.status === 'FAIL'
          ? 'TREE_GEOMETRY_EVIDENCE_FAILED'
          : 'TREE_GEOMETRY_EVIDENCE_VERIFIED',
      severity:
        report.status === 'FAIL'
          ? 'error'
          : warningIssueCount > 0
            ? 'warning'
            : 'info',
      timestamp: observedAt,
      metadata: {
        authenticatedSession: true,
        geometryObservationId,
        failureSourceSchema:
          TREE_GEOMETRY_FAILURE_SOURCE_SCHEMA,
        status: report.status,
        issueCount: report.issueCount,
        fatalIssueCount,
        warningIssueCount,
        fatalKnownIssueCount:
          failureSources.fatalKnownIssueCount,
        fatalUnknownIssueCount:
          failureSources.fatalUnknownIssueCount,
        informationalIssueCount:
          failureSources.informationalIssueCount,
        passIssueCount:
          failureSources.passIssueCount,
        unclassifiedSeverityCount:
          failureSources.unclassifiedSeverityCount,
        routeRejectedNonadjacentGenerationCount:
          failureSources
            .routeRejectedNonadjacentGenerationCount,
        routeRejectedRootBoundaryCount:
          failureSources
            .routeRejectedRootBoundaryCount,
        routeRejectedObstacleIntersectionCount:
          failureSources
            .routeRejectedObstacleIntersectionCount,
        routeRejectedUnclassifiedCount:
          failureSources
            .routeRejectedUnclassifiedCount,
        fatalRuleCounts: {
          ...failureSources.fatalRuleCounts,
        },
        parentConnectorMissingCount,
        parentConnectorUnroutedCount,
        routeRejectedCount,
        routeMissingCount,
        parentEndpointAuthorityFailureCount,
        cardCount: report.cardCount,
        edgeCount: report.edgeCount,
        spouseLinkCount: report.spouseLinkCount,
        companionChipCount: report.companionChipCount,
        sharedEvidenceId: sharedEvidence?.id ?? null,
        readOnly: true,
        repairExecuted: false,
      },
    });

    runtimeEvidenceRecorded = true;
  } catch {
    runtimeEvidenceRecorded = false;
  }

  return {
    readOnly: true,
    repairExecuted: false,
    authenticatedSession: true,
    published: sharedEvidence !== null,
    status: report.status,
    issueCount: report.issueCount,
    fatalIssueCount,
    warningIssueCount,
    failureSources,
    parentConnectorMissingCount,
    parentConnectorUnroutedCount,
    routeRejectedCount,
    routeMissingCount,
    parentEndpointAuthorityFailureCount,
    sharedEvidenceId: sharedEvidence?.id ?? null,
    runtimeEvidenceRecorded,
    runtimeEvidencePublished:
      sharedEvidence !== null,
  };
}


export interface TreeRuntimeLegacyAncestryCausalEvidencePublication {
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly authenticatedSession: boolean;
  readonly published: boolean;

  readonly evidenceStatus:
    LegacyAncestryCausalLocalization['evidenceStatus'];

  readonly rootBoundaryRejectedCount: number;

  readonly rootBoundaryMaternalCount: number;
  readonly rootBoundaryPaternalCount: number;
  readonly rootBoundaryOtherRootCount: number;

  readonly rootBoundaryViewerTerminalCandidateCount: number;
  readonly rootBoundaryOrdinaryCount: number;
  readonly rootBoundaryCandidateIneligibleCount: number;
  readonly rootBoundaryEligibleContradictionCount: number;

  readonly rootBoundaryAnyDivergentCount: number;
  readonly horizontalViolationCount: number;
  readonly horizontalAnyDivergentCount: number;
  readonly invalidJoinCount: number;

  readonly sourceMutationAllowed: false;
  readonly privateIdentityPublished: false;

  readonly sharedEvidenceId:
    string | null;

  readonly runtimeEvidenceRecorded:
    boolean;

  readonly runtimeEvidencePublished:
    boolean;
}

export function publishTreeRuntimeLegacyAncestryCausalLocalizationEvidence(
  localization:
    LegacyAncestryCausalLocalization,

  authenticatedSession:
    boolean,

  geometryObservationId:
    string,
): TreeRuntimeLegacyAncestryCausalEvidencePublication {
  const horizontalViolationCount =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (total, rule) =>
        total +
        localization
          .horizontal[rule]
          .violationCount,
      0,
    );

  const horizontalAnyDivergentCount =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (total, rule) =>
        total +
        localization
          .horizontal[rule]
          .anyDivergentCount,
      0,
    );

  const base = {
    readOnly:
      true as const,

    repairExecuted:
      false as const,

    evidenceStatus:
      localization.evidenceStatus,

    rootBoundaryRejectedCount:
      localization.rootBoundaryRejectedCount,

    rootBoundaryMaternalCount:
      localization.rootBoundaryMaternalCount,

    rootBoundaryPaternalCount:
      localization.rootBoundaryPaternalCount,

    rootBoundaryOtherRootCount:
      localization.rootBoundaryOtherRootCount,

    rootBoundaryViewerTerminalCandidateCount:
      localization.rootBoundaryViewerTerminalCandidateCount,

    rootBoundaryOrdinaryCount:
      localization.rootBoundaryOrdinaryCount,

    rootBoundaryCandidateIneligibleCount:
      localization.rootBoundaryCandidateIneligibleCount,

    rootBoundaryEligibleContradictionCount:
      localization.rootBoundaryEligibleContradictionCount,

    rootBoundaryAnyDivergentCount:
      localization.rootBoundaryAnyDivergentCount,

    horizontalViolationCount,

    horizontalAnyDivergentCount,

    invalidJoinCount:
      localization.invalidJoinCount,

    sourceMutationAllowed:
      false as const,

    privateIdentityPublished:
      false as const,
  };

  if (!authenticatedSession) {
    return Object.freeze({
      ...base,
      authenticatedSession:
        false,
      published:
        false,
      sharedEvidenceId:
        null,
      runtimeEvidenceRecorded:
        false,
      runtimeEvidencePublished:
        false,
    });
  }

  const observedAt =
    new Date().toISOString();

  const evidenceId =
    `tree-legacy-ancestry-causal-${Date.now()}-` +
    `${localization.rootBoundaryRejectedCount}`;

  const hasDivergence =
    localization
      .rootBoundaryAnyDivergentCount >
      0 ||
    horizontalAnyDivergentCount >
      0;

  const summary =
    localization.evidenceStatus ===
      'INVALID_EVIDENCE'
      ? 'Tree legacy-ancestry causal localization rejected invalid private join evidence.'
      : hasDivergence
        ? 'Tree legacy-ancestry causal localization observed legacy/canonical divergence within the failing geometry cohort.'
        : 'Tree legacy-ancestry causal localization observed no legacy/canonical divergence within the failing geometry cohort.';

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null =
      null;

  let runtimeEvidenceRecorded =
    false;

  try {
    sharedEvidence =
      publishGovernorRuntimeEvidence({
        domain:
          'tree',

        source:
          'TreeRuntimeLegacyAncestryResidueCausalLocalization',

        severity:
          localization.evidenceStatus ===
            'INVALID_EVIDENCE'
            ? 'fail'
            : hasDivergence
              ? 'warn'
              : 'info',

        summary,

        evidence: [
          `evidenceId=${evidenceId}`,
          `schema=${TREE_RUNTIME_LEGACY_ANCESTRY_CAUSAL_SCHEMA}`,
          'authenticatedSession=true',
          `geometryObservationId=${geometryObservationId}`,
          `evidenceStatus=${localization.evidenceStatus}`,
          `branchLegacyFallbackSelected=${localization.branchLegacyFallbackSelected}`,
          `layoutLegacyFallbackSelected=${localization.layoutLegacyFallbackSelected}`,
          `rootBoundaryRejectedCount=${localization.rootBoundaryRejectedCount}`,
          `rootBoundaryMaternalCount=${localization.rootBoundaryMaternalCount}`,
          `rootBoundaryPaternalCount=${localization.rootBoundaryPaternalCount}`,
          `rootBoundaryOtherRootCount=${localization.rootBoundaryOtherRootCount}`,
          `rootBoundaryViewerTerminalCandidateCount=${localization.rootBoundaryViewerTerminalCandidateCount}`,
          `rootBoundaryOrdinaryCount=${localization.rootBoundaryOrdinaryCount}`,
          `rootBoundaryCandidateIneligibleCount=${localization.rootBoundaryCandidateIneligibleCount}`,
          `rootBoundaryEligibleContradictionCount=${localization.rootBoundaryEligibleContradictionCount}`,
          ...CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS.map(
            reason =>
              `viewerTerminalFailure.${reason}=${localization.viewerTerminalFailureReasonCounts[reason]}`,
          ),
          `rootBoundaryBranchDivergentCount=${localization.rootBoundaryBranchDivergentCount}`,
          `rootBoundaryLayoutDivergentCount=${localization.rootBoundaryLayoutDivergentCount}`,
          `rootBoundaryAnyDivergentCount=${localization.rootBoundaryAnyDivergentCount}`,
          `rootBoundaryIndeterminateCount=${localization.rootBoundaryIndeterminateCount}`,
          `rootBoundaryParityCount=${localization.rootBoundaryParityCount}`,
          ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
            rule => [
              `horizontal.${rule}.violationCount=${localization.horizontal[rule].violationCount}`,
              `horizontal.${rule}.branchDivergentCount=${localization.horizontal[rule].branchDivergentCount}`,
              `horizontal.${rule}.layoutDivergentCount=${localization.horizontal[rule].layoutDivergentCount}`,
              `horizontal.${rule}.anyDivergentCount=${localization.horizontal[rule].anyDivergentCount}`,
              `horizontal.${rule}.indeterminateCount=${localization.horizontal[rule].indeterminateCount}`,
              `horizontal.${rule}.parityCount=${localization.horizontal[rule].parityCount}`,
            ],
          ),
          `horizontalViolationCount=${horizontalViolationCount}`,
          `horizontalAnyDivergentCount=${horizontalAnyDivergentCount}`,
          `invalidJoinCount=${localization.invalidJoinCount}`,
          'upstreamAuthorityProven=true',
          'overlapIsCausation=false',
          'causalAttributionAllowed=false',
          'sourceMutationAllowed=false',
          'privateIdentityPublished=false',
          `observedAt=${observedAt}`,
        ],

        owner:
          'Tree Governor',

        repairReady:
          false,
      });
  } catch {
    sharedEvidence =
      null;
  }

  try {
    recordRuntimeEvidence({
      id:
        evidenceId,

      governor:
        'tree-governor',

      source:
        'TreeRuntimeLegacyAncestryResidueCausalLocalization',

      event:
        localization.evidenceStatus ===
          'INVALID_EVIDENCE'
          ? 'TREE_LEGACY_ANCESTRY_CAUSAL_EVIDENCE_INVALID'
          : 'TREE_LEGACY_ANCESTRY_CAUSAL_EVIDENCE_OBSERVED',

      severity:
        localization.evidenceStatus ===
          'INVALID_EVIDENCE'
          ? 'error'
          : hasDivergence
            ? 'warning'
            : 'info',

      timestamp:
        observedAt,

      metadata: {
        schema:
          TREE_RUNTIME_LEGACY_ANCESTRY_CAUSAL_SCHEMA,

        authenticatedSession:
          true,

        evidenceStatus:
          localization.evidenceStatus,

        geometryObservationId,

        branchLegacyFallbackSelected:
          localization.branchLegacyFallbackSelected,

        layoutLegacyFallbackSelected:
          localization.layoutLegacyFallbackSelected,

        rootBoundaryRejectedCount:
          localization.rootBoundaryRejectedCount,

        rootBoundaryMaternalCount:
          localization.rootBoundaryMaternalCount,

        rootBoundaryPaternalCount:
          localization.rootBoundaryPaternalCount,

        rootBoundaryOtherRootCount:
          localization.rootBoundaryOtherRootCount,

        rootBoundaryViewerTerminalCandidateCount:
          localization.rootBoundaryViewerTerminalCandidateCount,

        rootBoundaryOrdinaryCount:
          localization.rootBoundaryOrdinaryCount,

        rootBoundaryCandidateIneligibleCount:
          localization.rootBoundaryCandidateIneligibleCount,

        rootBoundaryEligibleContradictionCount:
          localization.rootBoundaryEligibleContradictionCount,

        viewerTerminalFailureReasonCounts: {
          ...localization
            .viewerTerminalFailureReasonCounts,
        },

        rootBoundaryBranchDivergentCount:
          localization.rootBoundaryBranchDivergentCount,

        rootBoundaryLayoutDivergentCount:
          localization.rootBoundaryLayoutDivergentCount,

        rootBoundaryAnyDivergentCount:
          localization.rootBoundaryAnyDivergentCount,

        rootBoundaryIndeterminateCount:
          localization.rootBoundaryIndeterminateCount,

        rootBoundaryParityCount:
          localization.rootBoundaryParityCount,

        horizontal:
          Object.fromEntries(
            LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
              rule => [
                rule,
                {
                  ...localization
                    .horizontal[rule],
                },
              ],
            ),
          ),

        horizontalViolationCount,
        horizontalAnyDivergentCount,

        invalidJoinCount:
          localization.invalidJoinCount,

        upstreamAuthorityProven:
          true,

        overlapIsCausation:
          false,

        causalAttributionAllowed:
          false,

        sourceMutationAllowed:
          false,

        privateIdentityPublished:
          false,

        sharedEvidenceId:
          sharedEvidence?.id ??
          null,

        readOnly:
          true,

        repairExecuted:
          false,
      },
    });

    runtimeEvidenceRecorded =
      true;
  } catch {
    runtimeEvidenceRecorded =
      false;
  }

  return Object.freeze({
    ...base,

    authenticatedSession:
      true,

    published:
      sharedEvidence !== null,

    sharedEvidenceId:
      sharedEvidence?.id ??
      null,

    runtimeEvidenceRecorded,

    runtimeEvidencePublished:
      sharedEvidence !== null,
  });
}



export interface TreeRuntimeCanonicalBranchCounterfactualEvidencePublication {
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly authenticatedSession: boolean;
  readonly published: boolean;
  readonly comparisonAvailability:
    TreeRuntimeCanonicalBranchCounterfactualComparison['availability'];
  readonly sharedEvidenceId: string | null;
  readonly runtimeEvidenceRecorded: boolean;
  readonly runtimeEvidencePublished: boolean;
}

export function publishTreeRuntimeCanonicalBranchCounterfactualShadowEvidence(
  comparison:
    TreeRuntimeCanonicalBranchCounterfactualComparison,
  authenticatedSession:
    boolean,
  geometryObservationId:
    string,
): TreeRuntimeCanonicalBranchCounterfactualEvidencePublication {
  const base = {
    readOnly:
      true as const,
    repairExecuted:
      false as const,
    comparisonAvailability:
      comparison.availability,
  };

  if (!authenticatedSession) {
    return Object.freeze({
      ...base,
      authenticatedSession:
        false,
      published:
        false,
      sharedEvidenceId:
        null,
      runtimeEvidenceRecorded:
        false,
      runtimeEvidencePublished:
        false,
    });
  }

  const observedAt =
    new Date().toISOString();

  const evidenceId =
    `tree-canonical-branch-counterfactual-${Date.now()}-` +
    `${comparison.controlFatalCount}`;

  const summary =
    comparison.availability ===
      'INVALID_EVIDENCE'
      ? 'Canonical branch counterfactual shadow evidence failed control-parity validation.'
      : comparison.introducedFatalCount > 0
        ? 'Canonical branch counterfactual shadow introduced fatal failures; no source authority may change.'
        : comparison.resolvedFatalCount > 0
          ? 'Canonical branch counterfactual shadow resolved part of the exact production failure cohort.'
          : 'Canonical branch counterfactual shadow observed no exact production-failure resolution.';

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null =
      null;

  let runtimeEvidenceRecorded =
    false;

  try {
    sharedEvidence =
      publishGovernorRuntimeEvidence({
        domain:
          'tree',

        source:
          'TreeRuntimeCanonicalBranchCounterfactualShadowProjection',

        severity:
          comparison.availability ===
            'INVALID_EVIDENCE'
            ? 'fail'
            : comparison.introducedFatalCount > 0
              ? 'warn'
              : 'info',

        summary,

        evidence: [
          `evidenceId=${evidenceId}`,
          `schema=${TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA}`,
          'authenticatedSession=true',
          `geometryObservationId=${geometryObservationId}`,
          `comparisonAvailability=${comparison.availability}`,
          `controlParity=${comparison.controlParity}`,
          `controlWorldParity=${comparison.controlWorldParity}`,
          `controlFailureParity=${comparison.controlFailureParity}`,
          `controlFatalCount=${comparison.controlFatalCount}`,
          `shadowFatalCount=${comparison.shadowFatalCount}`,
          `resolvedFatalCount=${comparison.resolvedFatalCount}`,
          `persistedFatalCount=${comparison.persistedFatalCount}`,
          `introducedFatalCount=${comparison.introducedFatalCount}`,
          `indeterminateControlFatalCount=${comparison.indeterminateControlFatalCount}`,
          `rootBoundaryControlCount=${comparison.rootBoundary.control}`,
          `rootBoundaryResolvedCount=${comparison.rootBoundary.resolved}`,
          `rootBoundaryPersistedCount=${comparison.rootBoundary.persisted}`,
          `rootBoundaryIntroducedCount=${comparison.rootBoundary.introduced}`,
          `rootBoundaryIndeterminateCount=${comparison.rootBoundary.indeterminate}`,
          ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
            rule => [
              `horizontal.${rule}.control=${comparison.horizontal[rule].control}`,
              `horizontal.${rule}.resolved=${comparison.horizontal[rule].resolved}`,
              `horizontal.${rule}.persisted=${comparison.horizontal[rule].persisted}`,
              `horizontal.${rule}.introduced=${comparison.horizontal[rule].introduced}`,
              `horizontal.${rule}.indeterminate=${comparison.horizontal[rule].indeterminate}`,
            ],
          ),
          `horizontalControlCount=${comparison.horizontalControlCount}`,
          `horizontalResolvedCount=${comparison.horizontalResolvedCount}`,
          `horizontalPersistedCount=${comparison.horizontalPersistedCount}`,
          `horizontalIntroducedCount=${comparison.horizontalIntroducedCount}`,
          `horizontalIndeterminateCount=${comparison.horizontalIndeterminateCount}`,
          `substitutedMemberCount=${comparison.substitutedMemberCount}`,
          `indeterminateMemberCount=${comparison.indeterminateMemberCount}`,
          `invalidJoinCount=${comparison.invalidJoinCount}`,
          'sourceMutationAllowed=false',
          'causalAttributionAllowed=false',
          'legacyRetirementAuthorized=false',
          'productionAuthorityChanged=false',
          'privateIdentityPublished=false',
          `observedAt=${observedAt}`,
        ],

        owner:
          'Tree Governor',

        repairReady:
          false,
      });
  } catch {
    sharedEvidence =
      null;
  }

  try {
    recordRuntimeEvidence({
      id:
        evidenceId,
      governor:
        'tree-governor',
      source:
        'TreeRuntimeCanonicalBranchCounterfactualShadowProjection',
      event:
        comparison.availability ===
          'INVALID_EVIDENCE'
          ? 'TREE_CANONICAL_BRANCH_COUNTERFACTUAL_INVALID'
          : 'TREE_CANONICAL_BRANCH_COUNTERFACTUAL_OBSERVED',
      severity:
        comparison.availability ===
          'INVALID_EVIDENCE'
          ? 'error'
          : comparison.introducedFatalCount > 0
            ? 'warning'
            : 'info',
      timestamp:
        observedAt,
      metadata: {
        schema:
          TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
        authenticatedSession:
          true,
        geometryObservationId,
        comparisonAvailability:
          comparison.availability,
        controlParity:
          comparison.controlParity,
        controlWorldParity:
          comparison.controlWorldParity,
        controlFailureParity:
          comparison.controlFailureParity,
        controlFatalCount:
          comparison.controlFatalCount,
        shadowFatalCount:
          comparison.shadowFatalCount,
        resolvedFatalCount:
          comparison.resolvedFatalCount,
        persistedFatalCount:
          comparison.persistedFatalCount,
        introducedFatalCount:
          comparison.introducedFatalCount,
        indeterminateControlFatalCount:
          comparison.indeterminateControlFatalCount,
        rootBoundary: {
          ...comparison.rootBoundary,
        },
        horizontal:
          Object.fromEntries(
            LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
              rule => [
                rule,
                {
                  ...comparison.horizontal[rule],
                },
              ],
            ),
          ),
        horizontalControlCount:
          comparison.horizontalControlCount,
        horizontalResolvedCount:
          comparison.horizontalResolvedCount,
        horizontalPersistedCount:
          comparison.horizontalPersistedCount,
        horizontalIntroducedCount:
          comparison.horizontalIntroducedCount,
        horizontalIndeterminateCount:
          comparison.horizontalIndeterminateCount,
        substitutedMemberCount:
          comparison.substitutedMemberCount,
        indeterminateMemberCount:
          comparison.indeterminateMemberCount,
        invalidJoinCount:
          comparison.invalidJoinCount,
        sourceMutationAllowed:
          false,
        causalAttributionAllowed:
          false,
        legacyRetirementAuthorized:
          false,
        productionAuthorityChanged:
          false,
        privateIdentityPublished:
          false,
        sharedEvidenceId:
          sharedEvidence?.id ??
          null,
        readOnly:
          true,
        repairExecuted:
          false,
      },
    });

    runtimeEvidenceRecorded =
      true;
  } catch {
    runtimeEvidenceRecorded =
      false;
  }

  return Object.freeze({
    ...base,
    authenticatedSession:
      true,
    published:
      sharedEvidence !== null,
    sharedEvidenceId:
      sharedEvidence?.id ??
      null,
    runtimeEvidenceRecorded,
    runtimeEvidencePublished:
      sharedEvidence !== null,
  });
}



export interface TreeRuntimeBranchLayoutInteractionEvidencePublication {
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;

  readonly authenticatedSession: boolean;
  readonly published: boolean;

  readonly comparisonAvailability:
    TreeRuntimeBranchLayoutInteractionFourCellComparison['availability'];

  readonly controlParity: boolean;
  readonly controlWorldParity: boolean;
  readonly controlFailureParity: boolean;
  readonly cellBReferenceCoherent: boolean;

  readonly sourceMutationAllowed: false;
  readonly causalAttributionAllowed: false;
  readonly repairAuthorized: false;
  readonly legacyRetirementAuthorized: false;
  readonly productionAuthorityChanged: false;
  readonly privateIdentityPublished: false;

  readonly sharedEvidenceId: string | null;
  readonly runtimeEvidenceRecorded: boolean;
  readonly runtimeEvidencePublished: boolean;
}

export function publishTreeRuntimeBranchLayoutInteractionCounterfactualEvidence(
  comparison:
    TreeRuntimeBranchLayoutInteractionFourCellComparison,
  authenticatedSession:
    boolean,
  geometryObservationId:
    string,
): TreeRuntimeBranchLayoutInteractionEvidencePublication {
  const base = {
    readOnly: true as const,
    repairExecuted: false as const,
    repairReady: false as const,

    comparisonAvailability:
      comparison.availability,

    controlParity:
      comparison.controlParity,

    controlWorldParity:
      comparison.controlWorldParity,

    controlFailureParity:
      comparison.controlFailureParity,

    cellBReferenceCoherent:
      comparison.cellBReferenceCoherent,

    sourceMutationAllowed: false as const,
    causalAttributionAllowed: false as const,
    repairAuthorized: false as const,
    legacyRetirementAuthorized: false as const,
    productionAuthorityChanged: false as const,
    privateIdentityPublished: false as const,
  };

  const authorityCoherent =
    comparison.schema ===
      TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA &&
    comparison.availability ===
      'OBSERVED' &&
    comparison.controlParity ===
      true &&
    comparison.controlWorldParity ===
      true &&
    comparison.controlFailureParity ===
      true &&
    comparison.cellBReferenceCoherent ===
      true &&
    comparison.cellB.availability ===
      'OBSERVED' &&
    comparison.cellB.schema ===
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA &&
    comparison.cellB.controlParity ===
      true &&
    comparison.cellB.controlWorldParity ===
      true &&
    comparison.cellB.controlFailureParity ===
      true &&
    comparison.cellB.sourceMutationAllowed ===
      false &&
    comparison.cellB.causalAttributionAllowed ===
      false &&
    comparison.cellB.legacyRetirementAuthorized ===
      false &&
    comparison.cellB.productionAuthorityChanged ===
      false &&
    comparison.cellB.privateIdentityPublished ===
      false &&
    comparison.cellB.readOnly ===
      true &&
    comparison.cellB.repairExecuted ===
      false &&
    comparison.cellC.availability ===
      'OBSERVED' &&
    comparison.cellD.availability ===
      'OBSERVED' &&
    comparison.semanticSubstitutedMemberCount >
      0 &&
    comparison.layoutSubstitutedMemberCount >
      0 &&
    comparison.sourceMutationAllowed ===
      false &&
    comparison.causalAttributionAllowed ===
      false &&
    comparison.repairAuthorized ===
      false &&
    comparison.legacyRetirementAuthorized ===
      false &&
    comparison.productionAuthorityChanged ===
      false &&
    comparison.privateIdentityPublished ===
      false &&
    comparison.readOnly ===
      true &&
    comparison.repairExecuted ===
      false;

  const correlationValid =
    typeof geometryObservationId ===
      'string' &&
    geometryObservationId.trim().length >
      0;

  if (
    !authenticatedSession ||
    !authorityCoherent ||
    !correlationValid
  ) {
    return Object.freeze({
      ...base,
      authenticatedSession:
        authenticatedSession === true,
      published: false,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    });
  }

  const ruleEvidence = (
    prefix:
      string,
    counts:
      TreeRuntimeBranchLayoutInteractionFourCellComparison[
        'cellC'
      ]['rootBoundary'],
  ): readonly string[] => [
    prefix + '.control=' +
      counts.control,
    prefix + '.resolved=' +
      counts.resolved,
    prefix + '.persisted=' +
      counts.persisted,
    prefix + '.introduced=' +
      counts.introduced,
    prefix + '.indeterminate=' +
      counts.indeterminate,
  ];

  const horizontalEvidence = (
    prefix:
      string,
    horizontal:
      TreeRuntimeBranchLayoutInteractionFourCellComparison[
        'cellC'
      ]['horizontal'],
  ): readonly string[] =>
    LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
      rule =>
        ruleEvidence(
          prefix + '.' + rule,
          horizontal[rule],
        ),
    );

  const aggregateRuleCounts = (
    counts:
      TreeRuntimeBranchLayoutInteractionFourCellComparison[
        'cellC'
      ]['rootBoundary'],
  ) =>
    Object.freeze({
      control: counts.control,
      resolved: counts.resolved,
      persisted: counts.persisted,
      introduced: counts.introduced,
      indeterminate: counts.indeterminate,
    });

  const aggregateHorizontal = (
    horizontal:
      TreeRuntimeBranchLayoutInteractionFourCellComparison[
        'cellC'
      ]['horizontal'],
  ) =>
    Object.freeze(
      Object.fromEntries(
        LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
          rule => [
            rule,
            aggregateRuleCounts(
              horizontal[rule],
            ),
          ],
        ),
      ),
    );

  const aggregateCell = (
    cell:
      TreeRuntimeBranchLayoutInteractionFourCellComparison[
        'cellC'
      ],
  ) =>
    Object.freeze({
      availability:
        cell.availability,
      shadowFatalCount:
        cell.shadowFatalCount,
      resolvedFatalCount:
        cell.resolvedFatalCount,
      persistedFatalCount:
        cell.persistedFatalCount,
      introducedFatalCount:
        cell.introducedFatalCount,
      indeterminateControlFatalCount:
        cell.indeterminateControlFatalCount,
      rootBoundary:
        aggregateRuleCounts(
          cell.rootBoundary,
        ),
      horizontal:
        aggregateHorizontal(
          cell.horizontal,
        ),
      horizontalControlCount:
        cell.horizontalControlCount,
      horizontalResolvedCount:
        cell.horizontalResolvedCount,
      horizontalPersistedCount:
        cell.horizontalPersistedCount,
      horizontalIntroducedCount:
        cell.horizontalIntroducedCount,
      horizontalIndeterminateCount:
        cell.horizontalIndeterminateCount,
      invalidJoinCount:
        cell.invalidJoinCount,
    });

  const cellA = Object.freeze({
    fatalCount:
      comparison.cellA.fatalCount,
    rootBoundaryCount:
      comparison.cellA.rootBoundaryCount,
    horizontalCount:
      comparison.cellA.horizontalCount,
    horizontal:
      Object.freeze(
        Object.fromEntries(
          LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
            rule => [
              rule,
              comparison.cellA.horizontal[
                rule
              ],
            ],
          ),
        ),
      ),
  });

  const cellB = Object.freeze({
    availability:
      comparison.cellB.availability,
    controlParity:
      comparison.cellB.controlParity,
    controlWorldParity:
      comparison.cellB.controlWorldParity,
    controlFailureParity:
      comparison.cellB.controlFailureParity,
    controlFatalCount:
      comparison.cellB.controlFatalCount,
    shadowFatalCount:
      comparison.cellB.shadowFatalCount,
    resolvedFatalCount:
      comparison.cellB.resolvedFatalCount,
    persistedFatalCount:
      comparison.cellB.persistedFatalCount,
    introducedFatalCount:
      comparison.cellB.introducedFatalCount,
    indeterminateControlFatalCount:
      comparison.cellB.indeterminateControlFatalCount,
    rootBoundary:
      aggregateRuleCounts(
        comparison.cellB.rootBoundary,
      ),
    horizontal:
      aggregateHorizontal(
        comparison.cellB.horizontal,
      ),
    horizontalControlCount:
      comparison.cellB.horizontalControlCount,
    horizontalResolvedCount:
      comparison.cellB.horizontalResolvedCount,
    horizontalPersistedCount:
      comparison.cellB.horizontalPersistedCount,
    horizontalIntroducedCount:
      comparison.cellB.horizontalIntroducedCount,
    horizontalIndeterminateCount:
      comparison.cellB.horizontalIndeterminateCount,
    substitutedMemberCount:
      comparison.cellB.substitutedMemberCount,
    indeterminateMemberCount:
      comparison.cellB.indeterminateMemberCount,
    invalidJoinCount:
      comparison.cellB.invalidJoinCount,
  });

  const cellC =
    aggregateCell(
      comparison.cellC,
    );

  const cellD =
    aggregateCell(
      comparison.cellD,
    );

  const observedAt =
    new Date().toISOString();

  const evidenceId =
    'tree-branch-layout-interaction-' +
    Date.now() +
    '-' +
    comparison.cellA.fatalCount;

  const introduced =
    comparison.cellC
      .introducedFatalCount >
      0 ||
    comparison.cellD
      .introducedFatalCount >
      0;

  const resolved =
    comparison.cellC
      .resolvedFatalCount >
      0 ||
    comparison.cellD
      .resolvedFatalCount >
      0;

  const summary =
    introduced
      ? 'Branch/layout four-cell counterfactual observed introduced fatal failures in one or more shadow cells; causal attribution and repair remain blocked.'
      : resolved
        ? 'Branch/layout four-cell counterfactual observed exact production-failure resolution in one or more shadow cells; causal attribution and repair remain blocked.'
        : 'Branch/layout four-cell counterfactual observed no exact production-failure resolution; causal attribution and repair remain blocked.';

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null =
      null;

  let runtimeEvidenceRecorded =
    false;

  try {
    sharedEvidence =
      publishGovernorRuntimeEvidence({
        domain: 'tree',

        source:
          'TreeRuntimeBranchLayoutInteractionCounterfactualComparison',

        severity:
          introduced
            ? 'warn'
            : 'info',

        summary,

        evidence: [
          'evidenceId=' +
            evidenceId,
          'schema=' +
            TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA,
          'authenticatedSession=true',
          'geometryObservationId=' +
            geometryObservationId,

          'comparisonAvailability=' +
            comparison.availability,
          'controlParity=' +
            comparison.controlParity,
          'controlWorldParity=' +
            comparison.controlWorldParity,
          'controlFailureParity=' +
            comparison.controlFailureParity,
          'cellBReferenceCoherent=' +
            comparison.cellBReferenceCoherent,

          'cellA.fatalCount=' +
            comparison.cellA.fatalCount,
          'cellA.rootBoundaryCount=' +
            comparison.cellA.rootBoundaryCount,
          'cellA.horizontalCount=' +
            comparison.cellA.horizontalCount,

          ...LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
            rule =>
              'cellA.horizontal.' +
              rule +
              '=' +
              comparison.cellA.horizontal[
                rule
              ],
          ),

          'cellB.availability=' +
            comparison.cellB.availability,
          'cellB.controlFatalCount=' +
            comparison.cellB.controlFatalCount,
          'cellB.shadowFatalCount=' +
            comparison.cellB.shadowFatalCount,
          'cellB.resolvedFatalCount=' +
            comparison.cellB.resolvedFatalCount,
          'cellB.persistedFatalCount=' +
            comparison.cellB.persistedFatalCount,
          'cellB.introducedFatalCount=' +
            comparison.cellB.introducedFatalCount,
          'cellB.indeterminateControlFatalCount=' +
            comparison.cellB.indeterminateControlFatalCount,

          ...ruleEvidence(
            'cellB.rootBoundary',
            comparison.cellB.rootBoundary,
          ),

          ...horizontalEvidence(
            'cellB.horizontal',
            comparison.cellB.horizontal,
          ),

          'cellC.availability=' +
            comparison.cellC.availability,
          'cellC.shadowFatalCount=' +
            comparison.cellC.shadowFatalCount,
          'cellC.resolvedFatalCount=' +
            comparison.cellC.resolvedFatalCount,
          'cellC.persistedFatalCount=' +
            comparison.cellC.persistedFatalCount,
          'cellC.introducedFatalCount=' +
            comparison.cellC.introducedFatalCount,
          'cellC.indeterminateControlFatalCount=' +
            comparison.cellC.indeterminateControlFatalCount,

          ...ruleEvidence(
            'cellC.rootBoundary',
            comparison.cellC.rootBoundary,
          ),

          ...horizontalEvidence(
            'cellC.horizontal',
            comparison.cellC.horizontal,
          ),

          'cellD.availability=' +
            comparison.cellD.availability,
          'cellD.shadowFatalCount=' +
            comparison.cellD.shadowFatalCount,
          'cellD.resolvedFatalCount=' +
            comparison.cellD.resolvedFatalCount,
          'cellD.persistedFatalCount=' +
            comparison.cellD.persistedFatalCount,
          'cellD.introducedFatalCount=' +
            comparison.cellD.introducedFatalCount,
          'cellD.indeterminateControlFatalCount=' +
            comparison.cellD.indeterminateControlFatalCount,

          ...ruleEvidence(
            'cellD.rootBoundary',
            comparison.cellD.rootBoundary,
          ),

          ...horizontalEvidence(
            'cellD.horizontal',
            comparison.cellD.horizontal,
          ),

          'semanticSubstitutedMemberCount=' +
            comparison.semanticSubstitutedMemberCount,
          'semanticIndeterminateMemberCount=' +
            comparison.semanticIndeterminateMemberCount,
          'layoutSubstitutedMemberCount=' +
            comparison.layoutSubstitutedMemberCount,
          'layoutIndeterminateMemberCount=' +
            comparison.layoutIndeterminateMemberCount,

          'sourceMutationAllowed=false',
          'causalAttributionAllowed=false',
          'repairAuthorized=false',
          'legacyRetirementAuthorized=false',
          'productionAuthorityChanged=false',
          'privateIdentityPublished=false',
          'repairReady=false',
          'readOnly=true',
          'repairExecuted=false',

          'observedAt=' +
            observedAt,
        ],

        owner:
          'Tree Governor',

        repairReady:
          false,
      });
  } catch {
    sharedEvidence =
      null;
  }

  try {
    recordRuntimeEvidence({
      id:
        evidenceId,

      governor:
        'tree-governor',

      source:
        'TreeRuntimeBranchLayoutInteractionCounterfactualComparison',

      event:
        'TREE_BRANCH_LAYOUT_INTERACTION_COUNTERFACTUAL_OBSERVED',

      severity:
        introduced
          ? 'warning'
          : 'info',

      timestamp:
        observedAt,

      metadata: {
        schema:
          TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA,

        authenticatedSession:
          true,

        geometryObservationId,

        comparisonAvailability:
          comparison.availability,

        controlParity:
          comparison.controlParity,

        controlWorldParity:
          comparison.controlWorldParity,

        controlFailureParity:
          comparison.controlFailureParity,

        cellBReferenceCoherent:
          comparison.cellBReferenceCoherent,

        cellA,
        cellB,
        cellC,
        cellD,

        semanticSubstitutedMemberCount:
          comparison.semanticSubstitutedMemberCount,

        semanticIndeterminateMemberCount:
          comparison.semanticIndeterminateMemberCount,

        layoutSubstitutedMemberCount:
          comparison.layoutSubstitutedMemberCount,

        layoutIndeterminateMemberCount:
          comparison.layoutIndeterminateMemberCount,

        sourceMutationAllowed:
          false,

        causalAttributionAllowed:
          false,

        repairAuthorized:
          false,

        legacyRetirementAuthorized:
          false,

        productionAuthorityChanged:
          false,

        privateIdentityPublished:
          false,

        repairReady:
          false,

        readOnly:
          true,

        repairExecuted:
          false,

        sharedEvidenceId:
          sharedEvidence?.id ??
          null,
      },
    });

    runtimeEvidenceRecorded =
      true;
  } catch {
    runtimeEvidenceRecorded =
      false;
  }

  return Object.freeze({
    ...base,

    authenticatedSession:
      true,

    published:
      sharedEvidence !== null,

    sharedEvidenceId:
      sharedEvidence?.id ??
      null,

    runtimeEvidenceRecorded,

    runtimeEvidencePublished:
      sharedEvidence !== null,
  });
}


export interface TreeRuntimeConvergenceEvidenceInput {
  readonly authenticatedSession: boolean;
  readonly convergence:
    CanonicalTreeRuntimeAuthorityConvergenceResult;

  readonly selectorDiagnostics:
    TreeRuntimeSelectorDiagnostics;

  readonly selectorDiscrepancyClassification:
    TreeRuntimeSelectorDiscrepancyClassification;

  readonly selectorDiscrepancySourceClassification:
    TreeRuntimeSelectorDiscrepancySourceClassification;

  readonly source: 'FamilyTreeView';
}

export interface TreeRuntimeConvergenceEvidencePublication {
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly authenticatedSession: boolean;
  readonly published: boolean;
  readonly convergenceStatus:
    CanonicalTreeRuntimeAuthorityConvergenceResult['status'];
  readonly containmentStatus:
    CanonicalTreeRuntimeContainmentStatus;
  readonly containmentAction:
    CanonicalTreeRuntimeContainmentAction;
  readonly blockerCount: number;
  readonly sharedEvidenceId: string | null;
  readonly runtimeEvidenceRecorded: boolean;
  readonly runtimeEvidencePublished: boolean;
}

export function publishTreeRuntimeConvergenceEvidence(
  input:
    TreeRuntimeConvergenceEvidenceInput,
): TreeRuntimeConvergenceEvidencePublication {
  const containment =
    resolveCanonicalTreeRuntimeConvergenceContainment(
      input.convergence,
    );

  if (!input.authenticatedSession) {
    return Object.freeze({
      readOnly: true,
      repairExecuted: false,
      authenticatedSession: false,
      published: false,
      convergenceStatus:
        input.convergence.status,
      containmentStatus:
        containment.status,
      containmentAction:
        containment.action,
      blockerCount:
        input.convergence.blockers.length,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    });
  }

  const observedAt =
    new Date().toISOString();

  const evidenceId =
    `tree-convergence-${Date.now()}-${input.convergence.productionVisibleMemberIds.length}`;

  const severity =
    containment.status === 'UNSAFE'
      ? 'fail'
      : containment.status === 'DEGRADED'
        ? 'warn'
        : 'info';

  const runtimeSeverity =
    containment.status === 'UNSAFE'
      ? 'error'
      : containment.status === 'DEGRADED'
        ? 'warning'
        : 'info';

  const summary =
    containment.status === 'READY'
      ? `Authenticated Tree runtime convergence verified across ${input.convergence.productionVisibleMemberIds.length} visible member(s).`
      : `Authenticated Tree runtime convergence classified ${containment.status} with ${input.convergence.blockers.length} blocker(s).`;

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null = null;

  let runtimeEvidenceRecorded = false;

  try {
    sharedEvidence =
      publishGovernorRuntimeEvidence({
        domain: 'tree',
        source:
          'TreeRuntimeConvergenceEvidence',
        severity,
        summary,
        evidence: [
          `evidenceId=${evidenceId}`,
          `authenticatedSession=true`,
          `convergenceStatus=${input.convergence.status}`,
          `containmentStatus=${containment.status}`,
          `containmentAction=${containment.action}`,
          `blockerCount=${input.convergence.blockers.length}`,
          `blockers=${input.convergence.blockers.join(',') || 'none'}`,
          `productionVisibleMemberCount=${input.convergence.productionVisibleMemberIds.length}`,
          `missingProjectionCount=${input.convergence.missingProjectionMemberIds.length}`,
          `missingGeometryRoleCount=${input.convergence.missingGeometryRoleMemberIds.length}`,
          `missingConnectorCount=${input.convergence.missingConnectorEdgeIds.length}`,
          `observedAt=${observedAt}`,
          ...TREE_RUNTIME_SELECTOR_DIAGNOSTIC_KEYS.map(
            key =>
              `selector.${key}=${input.selectorDiagnostics[key]}`,
          ),
          ...TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS.map(
            key =>
              `discrepancy.${key}=${input.selectorDiscrepancyClassification[key]}`,
          ),
          ...TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS.map(
            key =>
              `sourceDiscrepancy.${key}=${input.selectorDiscrepancySourceClassification[key]}`,
          ),
        ],
        owner: 'Tree Governor',
        repairReady: false,
      });
  } catch {
    sharedEvidence = null;
  }

  try {
    recordRuntimeEvidence({
      id: evidenceId,
      governor: 'tree-governor',
      source:
        'TreeRuntimeConvergenceEvidence',
      event:
        containment.status === 'READY'
          ? 'TREE_RUNTIME_CONVERGENCE_READY'
          : containment.status === 'DEGRADED'
            ? 'TREE_RUNTIME_CONVERGENCE_DEGRADED'
            : 'TREE_RUNTIME_CONVERGENCE_UNSAFE',
      severity: runtimeSeverity,
      timestamp: observedAt,
      metadata: {
        authenticatedSession: true,
        convergenceStatus:
          input.convergence.status,
        containmentStatus:
          containment.status,
        containmentAction:
          containment.action,
        blockerCount:
          input.convergence.blockers.length,
        blockers: [
          ...input.convergence.blockers,
        ],
        productionVisibleMemberCount:
          input.convergence
            .productionVisibleMemberIds
            .length,
        missingProjectionCount:
          input.convergence
            .missingProjectionMemberIds
            .length,
        missingGeometryRoleCount:
          input.convergence
            .missingGeometryRoleMemberIds
            .length,
        missingConnectorCount:
          input.convergence
            .missingConnectorEdgeIds
            .length,
        sharedEvidenceId:
          sharedEvidence?.id ?? null,
        readOnly: true,
        repairExecuted: false,
      },
    });

    runtimeEvidenceRecorded = true;
  } catch {
    runtimeEvidenceRecorded = false;
  }

  try {
    const projection =
      projectTreeRuntimeConvergenceEvidence(
        {
          generatedAt:
            new Date().toISOString(),
          totalEvents:
            sharedEvidence ? 1 : 0,
          failures:
            sharedEvidence?.severity ===
              'fail'
              ? 1
              : 0,
          warnings:
            sharedEvidence?.severity ===
              'warn'
              ? 1
              : 0,
          repairReady: 0,
          events:
            sharedEvidence
              ? [sharedEvidence]
              : [],
        },
      );

    writeTreeRuntimeConvergenceSessionEvidence(
      projection,
    );
  } catch {
    // Session recovery is supplemental and fails closed.
  }

  return Object.freeze({
    readOnly: true,
    repairExecuted: false,
    authenticatedSession: true,
    published:
      sharedEvidence !== null ||
      runtimeEvidenceRecorded,
    convergenceStatus:
      input.convergence.status,
    containmentStatus:
      containment.status,
    containmentAction:
      containment.action,
    blockerCount:
      input.convergence.blockers.length,
    sharedEvidenceId:
      sharedEvidence?.id ?? null,
    runtimeEvidenceRecorded,
    runtimeEvidencePublished:
      sharedEvidence !== null,
  });
}


/*
 * CC-TREE-LIVE-003G2N
 *
 * Dedicated lifecycle-attribution runtime publication.
 *
 * This deliberately uses only the generic Governor evidence bus
 * and Runtime Evidence Engine. It does not write to the Tree
 * convergence projection or convergence session evidence store.
 */

export interface TreeRuntimeMissingParentLifecycleAttributionEvidenceInput {
  readonly authenticatedSession:
    boolean;

  readonly decision:
    unknown;

  readonly source:
    'FamilyGraphSection';
}

export interface TreeRuntimeMissingParentLifecycleAttributionEvidencePublication {
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;

  readonly authenticatedSession:
    boolean;

  readonly published:
    boolean;

  readonly attributionStatus:
    TreeRuntimeLifecycleAttributionStatus | null;

  readonly lifecycleCountsPublished:
    boolean;

  readonly sharedEvidenceId:
    string | null;

  readonly runtimeEvidenceRecorded:
    boolean;

  readonly runtimeEvidencePublished:
    boolean;
}

let treeRuntimeMissingParentLifecyclePublicationSequence =
  0;

export function publishTreeRuntimeMissingParentLifecycleAttributionEvidence(
  input:
    TreeRuntimeMissingParentLifecycleAttributionEvidenceInput,
): TreeRuntimeMissingParentLifecycleAttributionEvidencePublication {
  if (!input.authenticatedSession) {
    return Object.freeze({
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
      authenticatedSession: false,
      published: false,
      attributionStatus: null,
      lifecycleCountsPublished: false,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    });
  }

  const projection =
    projectTreeRuntimeMissingParentLifecycleAttributionPublication(
      input.decision,
    );

  if (!projection) {
    return Object.freeze({
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
      authenticatedSession: true,
      published: false,
      attributionStatus: null,
      lifecycleCountsPublished: false,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    });
  }

  const observedAt =
    new Date().toISOString();

  treeRuntimeMissingParentLifecyclePublicationSequence +=
    1;

  const evidenceId =
    `tree-missing-parent-lifecycle-${Date.now()}-${treeRuntimeMissingParentLifecyclePublicationSequence}`;

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null =
    null;

  let runtimeEvidenceRecorded =
    false;

  try {
    sharedEvidence =
      publishGovernorRuntimeEvidence({
        domain: 'tree',
        source:
          projection.source,
        severity:
          projection.sharedSeverity,
        summary:
          projection.summary,
        evidence: [
          `evidenceId=${evidenceId}`,
          'authenticatedSession=true',
          `producer=${input.source}`,
          ...projection.evidence,
          `observedAt=${observedAt}`,
        ],
        owner: 'Tree Governor',
        repairReady: false,
      });
  } catch {
    sharedEvidence =
      null;
  }

  try {
    recordRuntimeEvidence({
      id:
        evidenceId,
      governor:
        'tree-governor',
      source:
        projection.source,
      event:
        projection.event,
      severity:
        projection.runtimeSeverity,
      timestamp:
        observedAt,
      metadata: {
        ...projection.metadata,
        authenticatedSession: true,
        producer:
          input.source,
        sharedEvidenceId:
          sharedEvidence?.id ?? null,
        readOnly: true,
        repairExecuted: false,
        repairReady: false,
      },
    });

    runtimeEvidenceRecorded =
      true;
  } catch {
    runtimeEvidenceRecorded =
      false;
  }

  const published =
    sharedEvidence !== null ||
    runtimeEvidenceRecorded;

  /*
   * CC-TREE-LIVE-003G2O
   *
   * Operational observability is downstream of successful
   * 003G2N publication. It cannot create publication success
   * and failure here cannot revoke existing N success.
   */
  if (published) {
    try {
      publishTreeRuntimeMissingParentLifecycleOperationalObservation({
        decision:
          input.decision,
        observedAt,
      });
    } catch {
      // Read-only observability failure is non-authoritative.
    }
  }

  return Object.freeze({
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
    authenticatedSession: true,
    published,
    attributionStatus:
      projection.attributionStatus,
    lifecycleCountsPublished:
      projection.lifecycleCountsPublished,
    sharedEvidenceId:
      sharedEvidence?.id ?? null,
    runtimeEvidenceRecorded,
    runtimeEvidencePublished:
      sharedEvidence !== null,
  });
}

/*
 * CC-TREE-LIVE-003G2S
 *
 * Publish only the sanitized identity-parity projection.
 * Raw identities, nonce, commitment, and RPC envelope data
 * are never serialized into Governor or Runtime Evidence.
 */
export interface TreeRuntimeMissingParentIdentityParityEvidenceInput {
  readonly authenticatedSession: boolean;

  readonly observation:
    TreeRuntimeMissingParentIdentityBrowserObservation;

  readonly source:
    'FamilyGraphSection';
}

export interface TreeRuntimeMissingParentIdentityParityEvidencePublication {
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;

  readonly authenticatedSession:
    boolean;

  readonly published:
    boolean;

  readonly browserStatus:
    TreeRuntimeMissingParentIdentityBrowserObservation['status'] | null;

  readonly identityParityProven:
    boolean;

  readonly repairPlanningEligible:
    boolean;

  readonly sharedEvidenceId:
    string | null;

  readonly runtimeEvidenceRecorded:
    boolean;

  readonly runtimeEvidencePublished:
    boolean;
}

let treeRuntimeMissingParentIdentityParityPublicationSequence =
  0;

export function publishTreeRuntimeMissingParentIdentityParityEvidence(
  input:
    TreeRuntimeMissingParentIdentityParityEvidenceInput,
): TreeRuntimeMissingParentIdentityParityEvidencePublication {
  if (!input.authenticatedSession) {
    return Object.freeze({
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
      authenticatedSession: false,
      published: false,
      browserStatus: null,
      identityParityProven: false,
      repairPlanningEligible: false,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    });
  }

  const projection =
    projectTreeRuntimeMissingParentIdentityParityPublication(
      input.observation,
    );

  if (!projection) {
    return Object.freeze({
      readOnly: true,
      repairExecuted: false,
      repairReady: false,
      authenticatedSession: true,
      published: false,
      browserStatus: null,
      identityParityProven: false,
      repairPlanningEligible: false,
      sharedEvidenceId: null,
      runtimeEvidenceRecorded: false,
      runtimeEvidencePublished: false,
    });
  }

  const observedAt =
    new Date().toISOString();

  treeRuntimeMissingParentIdentityParityPublicationSequence +=
    1;

  const evidenceId =
    `tree-missing-parent-identity-parity-${Date.now()}-${treeRuntimeMissingParentIdentityParityPublicationSequence}`;

  let sharedEvidence:
    GovernorRuntimeEvidenceEvent | null =
    null;

  let runtimeEvidenceRecorded =
    false;

  try {
    sharedEvidence =
      publishGovernorRuntimeEvidence({
        domain: 'tree',
        source:
          projection.source,
        severity:
          projection.sharedSeverity,
        summary:
          projection.summary,
        evidence: [
          `evidenceId=${evidenceId}`,
          'authenticatedSession=true',
          `producer=${input.source}`,
          `milestone=${projection.milestone}`,
          ...projection.evidence,
          `observedAt=${observedAt}`,
        ],
        owner:
          'Tree Governor',
        repairReady:
          false,
      });
  } catch {
    sharedEvidence =
      null;
  }

  try {
    recordRuntimeEvidence({
      id:
        evidenceId,
      governor:
        'tree-governor',
      source:
        projection.source,
      event:
        projection.event,
      severity:
        projection.runtimeSeverity,
      timestamp:
        observedAt,
      metadata: {
        ...projection.metadata,
        milestone:
          projection.milestone,
        authenticatedSession:
          true,
        producer:
          input.source,
        sharedEvidenceId:
          sharedEvidence?.id ?? null,
        readOnly:
          true,
        repairExecuted:
          false,
        repairReady:
          false,
      },
    });

    runtimeEvidenceRecorded =
      true;
  } catch {
    runtimeEvidenceRecorded =
      false;
  }

  const published =
    sharedEvidence !== null ||
    runtimeEvidenceRecorded;

  return Object.freeze({
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
    authenticatedSession: true,
    published,
    browserStatus:
      projection.browserStatus,
    identityParityProven:
      projection.identityParityProven,
    repairPlanningEligible:
      projection.repairPlanningEligible,
    sharedEvidenceId:
      sharedEvidence?.id ?? null,
    runtimeEvidenceRecorded,
    runtimeEvidencePublished:
      sharedEvidence !== null,
  });
}
